import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { addressApi, neighborhoodApi, orderApi, couponApi } from '../api/services';
import { useCartStore, type CartItem } from '../store';
import { PAYMENT_METHODS } from '../hooks/useOrderHub';

const emptyAddr = { label: 'Ev', neighborhoodId: '', street: '', buildingNo: '', buildingName: '', floor: '', apartmentNo: '', description: '' };

function errMsg(e: any) {
  return e?.response?.data?.error || e?.response?.data?.errors?.[0] || e?.response?.data?.title || 'Bir hata oluştu.';
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { foodCart, marketCart, clearCart } = useCartStore();

  const [addressId, setAddressId] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [newAddr, setNewAddr] = useState(emptyAddr);
  const [payment, setPayment] = useState(1);
  const [notes, setNotes] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [couponInfo, setCouponInfo] = useState<{ code: string; discount: number } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { data: addresses = [] } = useQuery({
    queryKey: ['addresses'],
    queryFn: () => addressApi.getAll().then((r) => r.data),
  });
  const { data: neighborhoods = [] } = useQuery({
    queryKey: ['neighborhoods'],
    queryFn: () => neighborhoodApi.getAll().then((r) => r.data),
  });

  useEffect(() => {
    if (!addressId && addresses.length > 0) {
      setAddressId((addresses.find((a: any) => a.isDefault) ?? addresses[0]).id);
    }
    if (addresses.length === 0) setShowNew(true);
  }, [addresses, addressId]);

  const total = (c: CartItem[]) => c.reduce((s, i) => s + i.price * i.quantity, 0);
  const subTotal = total(foodCart) + total(marketCart);
  const selectedAddr = addresses.find((a: any) => a.id === addressId);
  const nb = neighborhoods.find((n: any) => n.id === selectedAddr?.neighborhoodId);
  const deliveryFee = nb?.deliveryFee ?? 0;
  const orderCount = (foodCart.length ? 1 : 0) + (marketCart.length ? 1 : 0);
  const grand = subTotal - (couponInfo?.discount ?? 0) + deliveryFee * orderCount;

  if (foodCart.length === 0 && marketCart.length === 0) {
    return (
      <div className="container" style={{ paddingTop: 32 }}>
        <h1>Ödeme</h1>
        <p className="text-muted">Sepetiniz boş.</p>
      </div>
    );
  }

  const saveAddress = async () => {
    const n = neighborhoods.find((x: any) => x.id === newAddr.neighborhoodId);
    if (!n || !newAddr.street || !newAddr.buildingNo) return toast.error('Mahalle, sokak ve bina no zorunlu.');
    try {
      const { data } = await addressApi.create({
        label: newAddr.label, city: n.city, district: n.district, neighborhoodId: n.id,
        street: newAddr.street, buildingNo: newAddr.buildingNo, buildingName: newAddr.buildingName || null,
        floor: newAddr.floor || null, apartmentNo: newAddr.apartmentNo || null, description: newAddr.description || null,
        isDefault: addresses.length === 0,
      });
      await qc.invalidateQueries({ queryKey: ['addresses'] });
      setAddressId(data.id);
      setShowNew(false);
      setNewAddr(emptyAddr);
      toast.success('Adres kaydedildi');
    } catch (e) { toast.error(errMsg(e)); }
  };

  const applyCoupon = async () => {
    try {
      const { data } = await couponApi.validate(couponCode.trim(), subTotal);
      setCouponInfo(data);
      toast.success(`Kupon uygulandı: -${data.discount.toFixed(2)} ₺`);
    } catch (e) { setCouponInfo(null); toast.error(errMsg(e)); }
  };

  const placeOrders = async () => {
    if (!addressId) return toast.error('Teslimat adresi seçin.');
    setSubmitting(true);
    const carts: { type: 1 | 2; key: 'food' | 'market'; items: CartItem[] }[] = [
      { type: 1 as const, key: 'food' as const, items: foodCart },
      { type: 2 as const, key: 'market' as const, items: marketCart },
    ].filter((c) => c.items.length > 0);

    let couponUsed = false;
    const numbers: string[] = [];
    for (const c of carts) {
      try {
        const { data } = await orderApi.create({
          addressId,
          type: c.type,
          paymentMethod: payment,
          items: c.items.map((i) => ({ productId: i.productId, quantity: i.quantity, notes: i.notes ?? null })),
          couponCode: !couponUsed && couponInfo ? couponInfo.code : null,
          scheduledAt: null,
          notes: notes || null,
        });
        if (couponInfo) couponUsed = true;
        numbers.push(data.orderNumber);
        clearCart(c.key);
      } catch (e) {
        toast.error(`${c.key === 'food' ? 'Yemek' : 'Market'} siparişi: ${errMsg(e)}`);
      }
    }
    setSubmitting(false);
    if (numbers.length > 0) {
      toast.success(`Siparişiniz alındı: ${numbers.join(', ')}`);
      navigate('/orders');
    }
  };

  return (
    <div className="container" style={{ paddingTop: 32, paddingBottom: 48 }}>
      <h1 style={{ marginBottom: 24 }}>Ödeme</h1>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <h3 style={{ marginBottom: 12 }}>Teslimat Adresi</h3>
            {addresses.map((a: any) => (
              <label key={a.id} style={{ display: 'flex', gap: 8, padding: '8px 0', cursor: 'pointer' }}>
                <input type="radio" checked={addressId === a.id} onChange={() => setAddressId(a.id)} />
                <span><b>{a.label}</b> — {a.neighborhoodName}, {a.street} No:{a.buildingNo}{a.buildingName ? ` ${a.buildingName}` : ''}{a.floor ? ` Kat:${a.floor}` : ''}{a.apartmentNo ? ` Daire:${a.apartmentNo}` : ''}</span>
              </label>
            ))}
            <button className="btn btn-sm" style={{ marginTop: 8 }} onClick={() => setShowNew(!showNew)}>
              {showNew ? 'Vazgeç' : '+ Yeni Adres'}
            </button>
            {showNew && (
              <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
                <input className="form-input" placeholder="Etiket (Ev, İş...)" value={newAddr.label} onChange={(e) => setNewAddr({ ...newAddr, label: e.target.value })} />
                <select className="form-input" value={newAddr.neighborhoodId} onChange={(e) => setNewAddr({ ...newAddr, neighborhoodId: e.target.value })}>
                  <option value="">Mahalle seçin</option>
                  {neighborhoods.filter((n: any) => n.isActive).map((n: any) => <option key={n.id} value={n.id}>{n.name}</option>)}
                </select>
                <input className="form-input" placeholder="Sokak / Cadde" value={newAddr.street} onChange={(e) => setNewAddr({ ...newAddr, street: e.target.value })} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 8 }}>
                  <input className="form-input" placeholder="Bina No *" value={newAddr.buildingNo} onChange={(e) => setNewAddr({ ...newAddr, buildingNo: e.target.value })} />
                  <input className="form-input" placeholder="Apartman / Bina İsmi" value={newAddr.buildingName} onChange={(e) => setNewAddr({ ...newAddr, buildingName: e.target.value })} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <input className="form-input" placeholder="Kat (Örn: 2)" value={newAddr.floor} onChange={(e) => setNewAddr({ ...newAddr, floor: e.target.value })} />
                  <input className="form-input" placeholder="Daire No (Örn: 5)" value={newAddr.apartmentNo} onChange={(e) => setNewAddr({ ...newAddr, apartmentNo: e.target.value })} />
                </div>
                <input className="form-input" placeholder="Adres tarifi (opsiyonel)" value={newAddr.description} onChange={(e) => setNewAddr({ ...newAddr, description: e.target.value })} />
                <button className="btn btn-primary" onClick={saveAddress}>Adresi Kaydet</button>
              </div>
            )}
          </div>

          <div className="card">
            <h3 style={{ marginBottom: 12 }}>Ödeme Yöntemi</h3>
            <select className="form-input" value={payment} onChange={(e) => setPayment(Number(e.target.value))}>
              <option value={1}>Kapıda Nakit</option>
              <option value={2}>Kapıda Kredi Kartı (POS)</option>
              <option value={3}>Online Kredi Kartı</option>
              <option value={4}>Metropol</option>
              <option value={5}>Setcard</option>
              <option value={6}>Edenred Ticket</option>
            </select>
            <textarea className="form-input" style={{ marginTop: 12 }} rows={2} placeholder="Sipariş notu (opsiyonel)" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>

        <div className="card" style={{ alignSelf: 'start' }}>
          <h3 style={{ marginBottom: 12 }}>Özet</h3>
          {[...foodCart, ...marketCart].map((i) => (
            <div key={i.productId} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
              <span>{i.quantity} × {i.name}</span><span>{(i.price * i.quantity).toFixed(2)} ₺</span>
            </div>
          ))}
          <hr style={{ margin: '12px 0', opacity: 0.2 }} />
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <input className="form-input" placeholder="Kupon kodu" value={couponCode} onChange={(e) => setCouponCode(e.target.value)} />
            <button className="btn btn-sm" onClick={applyCoupon} disabled={!couponCode.trim()}>Uygula</button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Ara Toplam</span><span>{subTotal.toFixed(2)} ₺</span></div>
          {couponInfo && <div style={{ display: 'flex', justifyContent: 'space-between', color: '#22c55e' }}><span>Kupon</span><span>-{couponInfo.discount.toFixed(2)} ₺</span></div>}
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Teslimat{orderCount > 1 ? ` (${orderCount} sipariş)` : ''}</span><span>{(deliveryFee * orderCount).toFixed(2)} ₺</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: '1.2rem', marginTop: 8 }}><span>Toplam</span><span>{grand.toFixed(2)} ₺</span></div>
          {orderCount > 1 && <p className="text-muted" style={{ fontSize: '0.8rem', marginTop: 8 }}>Yemek ve market ürünleri ayrı sipariş olarak oluşturulur.</p>}
          <button className="btn btn-primary" style={{ width: '100%', marginTop: 16 }} disabled={submitting || !addressId} onClick={placeOrders}>
            {submitting ? 'Gönderiliyor...' : 'Siparişi Onayla'}
          </button>
        </div>
      </div>
    </div>
  );
}
